import type { NotificationItem } from "../types/notifications";
import type { TradeStatisticsFilters } from "../types/trade";
import { APP_ORIGIN } from "@/constants/brand";
import { formatDisplayCurrency } from "./formatDisplayCurrency";
import { getSyncedDisplayCurrency } from "./displayCurrencySync";
import { MINI_STAT_LOSS_GRADIENT } from "./miniStatGradient";

const COUNT_GRADIENT = "linear-gradient(135deg, #38BBD1, #3EEAEC)";
/** Matches Trade Log “Active Trades” mini-stat. */
const ACTIVE_TRADES_GRADIENT = "linear-gradient(135deg, #F45B0B, #F49113)";

export type PerformanceSummaryMiniStat = {
  id: string;
  title: string;
  amount: string;
  backgroundGradient?: string;
  tooltip?: string;
};

export type PerformanceSummarySymbolRow = {
  symbol: string;
  count: number;
  total_pnl: number;
};

export type PerformanceSummaryViewModel = {
  miniStats: PerformanceSummaryMiniStat[];
  longShort: { long: number; short: number };
  winLoss: { wins: number; losses: number; scratch: number };
  symbols: PerformanceSummarySymbolRow[];
  maxDrawdown: number | null;
  /** When set, detail UI can fetch risk metrics for the drawdown chart. */
  riskFilters: TradeStatisticsFilters | null;
};

function coerceDataRecord(data: NotificationItem["data"]): Record<string, unknown> | null {
  if (data == null) return null;
  if (typeof data === "string") {
    try {
      const v = JSON.parse(data);
      if (v && typeof v === "object" && !Array.isArray(v)) {
        return v as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

function pickNum(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function pickStr(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function displayCurrencyFromData(obj: Record<string, unknown>): string {
  const raw = pickStr(obj, ["display_currency", "displayCurrency"]);
  if (raw && /^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();
  if (raw?.toUpperCase() === "XAU") return "XAU";
  return getSyncedDisplayCurrency();
}

function formatMoney(
  value: number,
  currency: string,
  options?: { showSign?: boolean }
): string {
  return formatDisplayCurrency(value, currency, { locale: "en-US", ...options });
}

function formatPercent(value: number): string {
  const normalized = value > 0 && value <= 1 ? value * 100 : value;
  return `${normalized.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
}

function formatLargestLoss(value: number, currency: string): string {
  return value < 0 ? formatMoney(value, currency) : "N/A";
}

function ymdFromIsoOrDate(raw: string): string | null {
  const t = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const ms = Date.parse(t);
  if (Number.isNaN(ms)) return null;
  return t.slice(0, 10);
}

/** Exclusive end → inclusive local_end_date (YYYY-MM-DD). */
function exclusiveEndToInclusiveYmd(exclusiveEnd: string): string | null {
  const ymd = ymdFromIsoOrDate(exclusiveEnd);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

function parseRiskFilters(obj: Record<string, unknown>): TradeStatisticsFilters | null {
  const account = pickStr(obj, ["broker_account_code", "brokerAccountCode", "account_code", "accountCode"]);
  if (!account) return null;

  const fromUrl = pickStr(obj, ["statistics_url", "statisticsUrl"]);
  if (fromUrl) {
    try {
      const u = new URL(fromUrl, APP_ORIGIN);
      const localStart = u.searchParams.get("local_start_date") || undefined;
      const localEnd = u.searchParams.get("local_end_date") || undefined;
      const timezone = u.searchParams.get("timezone") || pickStr(obj, ["timezone"]) || undefined;
      const brokerFromUrl = u.searchParams.get("broker_account_code") || account;
      if (localStart && localEnd) {
        return {
          broker_account_code: brokerFromUrl,
          local_start_date: localStart,
          local_end_date: localEnd,
          timezone,
        };
      }
    } catch {
      /* fall through */
    }
  }

  const startRaw = pickStr(obj, [
    "period_start_local",
    "periodStartLocal",
    "period_start",
    "periodStart",
    "local_start_date",
    "localStartDate",
  ]);
  const endRaw = pickStr(obj, [
    "period_end_local",
    "periodEndLocal",
    "period_end",
    "periodEnd",
    "local_end_date",
    "localEndDate",
  ]);
  if (!startRaw || !endRaw) return null;

  const localStart = ymdFromIsoOrDate(startRaw);
  const endLooksExclusive =
    Boolean(obj.period_end_local || obj.periodEndLocal) ||
    Boolean(obj.period_end || obj.periodEnd);
  const localEnd = endLooksExclusive
    ? exclusiveEndToInclusiveYmd(endRaw)
    : ymdFromIsoOrDate(endRaw);

  if (!localStart || !localEnd) return null;

  return {
    broker_account_code: account,
    local_start_date: localStart,
    local_end_date: localEnd,
    timezone: pickStr(obj, ["timezone"]),
  };
}

function parseSymbolBreakdown(obj: Record<string, unknown>): PerformanceSummarySymbolRow[] {
  const raw = obj.symbol_breakdown ?? obj.by_symbol ?? obj.symbols;
  let arr: unknown = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw) as unknown;
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const o = row as Record<string, unknown>;
      const symbol = pickStr(o, ["symbol", "ticker", "instrument"]);
      if (!symbol) return null;
      const count = pickNum(o, ["count", "trades", "trade_count"]) ?? 0;
      const total_pnl = pickNum(o, ["total_pnl", "totalPnl", "pnl", "net_pnl"]) ?? 0;
      return { symbol, count, total_pnl };
    })
    .filter((x): x is PerformanceSummarySymbolRow => x !== null);
}

/**
 * Structured view model for in-app performance summary detail
 * (`performance.summary.daily|weekly|monthly`).
 * Missing scalars become N/A mini stats so the detail layout stays complete.
 */
export function parsePerformanceSummaryViewModel(
  data: NotificationItem["data"]
): PerformanceSummaryViewModel {
  const empty: PerformanceSummaryViewModel = {
    miniStats: [],
    longShort: { long: 0, short: 0 },
    winLoss: { wins: 0, losses: 0, scratch: 0 },
    symbols: [],
    maxDrawdown: null,
    riskFilters: null,
  };

  const obj = coerceDataRecord(data);
  if (!obj) {
    return {
      ...empty,
      miniStats: buildEmptyMiniStats(),
    };
  }

  const currency = displayCurrencyFromData(obj);
  const miniStats: PerformanceSummaryMiniStat[] = [];

  const pushCount = (
    id: string,
    title: string,
    value: number | undefined,
    opts?: { tooltip?: string; backgroundGradient?: string }
  ) => {
    miniStats.push({
      id,
      title,
      amount: value === undefined ? "N/A" : String(Math.trunc(value)),
      backgroundGradient: opts?.backgroundGradient ?? COUNT_GRADIENT,
      tooltip: opts?.tooltip,
    });
  };

  const pushMoney = (
    id: string,
    title: string,
    value: number | undefined,
    opts?: { showSign?: boolean; backgroundGradient?: string; tooltip?: string; format?: (n: number) => string }
  ) => {
    miniStats.push({
      id,
      title,
      amount:
        value === undefined
          ? "N/A"
          : opts?.format
            ? opts.format(value)
            : formatMoney(value, currency, { showSign: opts?.showSign }),
      backgroundGradient: opts?.backgroundGradient,
      tooltip: opts?.tooltip,
    });
  };

  const totalTrades = pickNum(obj, ["total_trades", "totalTrades"]);
  const openTrades = pickNum(obj, ["open_trades", "openTrades"]);
  const closedTrades = pickNum(obj, ["closed_trades", "closedTrades", "trades_closed", "tradesClosed"]);
  const winRate = pickNum(obj, ["win_rate", "winRate"]);
  const totalPnl = pickNum(obj, ["total_pnl", "totalPnl", "net_pnl", "netPnl"]);
  const averageLoss = pickNum(obj, ["average_loss", "averageLoss"]);
  const expectancy = pickNum(obj, [
    "average_trade_pnl",
    "averageTradePnl",
    "avg_trade_pnl",
    "avgTradePnl",
    "expectancy",
    "average_per_trade_pnl",
  ]);
  const averageWin = pickNum(obj, ["average_win", "averageWin"]);
  const largestLoss = pickNum(obj, ["largest_loss", "largestLoss"]);
  const largestWin = pickNum(obj, ["largest_win", "largestWin"]);
  const totalReturn = pickNum(obj, [
    "total_return_pct",
    "total_return_percent",
    "totalReturnPct",
    "totalReturnPercent",
  ]);
  const riskReward = pickNum(obj, ["risk_reward_ratio", "riskRewardRatio", "risk_reward"]);
  const maxDrawdown = pickNum(obj, ["max_drawdown", "maxDrawdown"]) ?? null;

  // Grouped for a 4-column grid (headline → activity → quality → extremes).
  // Row 1: performance headline
  pushMoney("total_pnl", "Total P&L", totalPnl, {
    showSign: true,
    tooltip: "Net P&L for the summary period.",
  });
  miniStats.push({
    id: "total_return_pct",
    title: "Total return",
    amount: totalReturn === undefined ? "N/A" : formatPercent(totalReturn),
    backgroundGradient:
      totalReturn !== undefined && totalReturn < 0 ? MINI_STAT_LOSS_GRADIENT : undefined,
  });
  miniStats.push({
    id: "win_rate",
    title: "Win rate",
    amount: winRate === undefined ? "N/A" : formatPercent(winRate),
    backgroundGradient: COUNT_GRADIENT,
    tooltip: "Winning closed trades ÷ closed trades.",
  });
  pushMoney("expectancy", "Expectancy", expectancy, {
    showSign: true,
    tooltip: "Average P&L per trade (same as average trade P&L).",
  });

  // Row 2: trade activity
  pushCount("total_trades", "Total trades", totalTrades, {
    tooltip: "Open and closed trades in this period.",
  });
  pushCount("closed_trades", "Closed trades", closedTrades);
  pushCount("open_trades", "Active trades", openTrades, {
    backgroundGradient: ACTIVE_TRADES_GRADIENT,
  });
  miniStats.push({
    id: "risk_reward_ratio",
    title: "Risk / reward",
    amount: riskReward === undefined ? "N/A" : `${riskReward.toFixed(2)}:1`,
    backgroundGradient: COUNT_GRADIENT,
    tooltip: "Average win ÷ average loss (absolute).",
  });

  // Row 3: averages + extremes (max drawdown is chart-only)
  pushMoney("average_win", "Average win", averageWin, { showSign: true });
  pushMoney("average_loss", "Average loss", averageLoss, {
    backgroundGradient: MINI_STAT_LOSS_GRADIENT,
  });
  pushMoney("largest_win", "Largest win", largestWin, { showSign: true });
  pushMoney("largest_loss", "Largest loss", largestLoss, {
    backgroundGradient: MINI_STAT_LOSS_GRADIENT,
    format: (n) => formatLargestLoss(n, currency),
  });

  const long = pickNum(obj, ["long_trades", "longTrades"]);
  const short = pickNum(obj, ["short_trades", "shortTrades"]);
  const longShort = { long: long ?? 0, short: short ?? 0 };

  const wins = pickNum(obj, ["winning_trades", "winningTrades"]);
  const losses = pickNum(obj, ["losing_trades", "losingTrades"]);
  const scratch = pickNum(obj, ["scratch_trades", "scratchTrades"]);
  const winLoss = {
    wins: wins ?? 0,
    losses: losses ?? 0,
    scratch: scratch ?? 0,
  };

  return {
    miniStats,
    longShort,
    winLoss,
    symbols: parseSymbolBreakdown(obj),
    maxDrawdown,
    riskFilters: parseRiskFilters(obj),
  };
}

const EMPTY_MINI_STAT_DEFS: Array<{
  id: string;
  title: string;
  backgroundGradient?: string;
  tooltip?: string;
}> = [
  { id: "total_pnl", title: "Total P&L", tooltip: "Net P&L for the summary period." },
  { id: "total_return_pct", title: "Total return" },
  {
    id: "win_rate",
    title: "Win rate",
    backgroundGradient: COUNT_GRADIENT,
    tooltip: "Winning closed trades ÷ closed trades.",
  },
  {
    id: "expectancy",
    title: "Expectancy",
    tooltip: "Average P&L per trade (same as average trade P&L).",
  },
  {
    id: "total_trades",
    title: "Total trades",
    backgroundGradient: COUNT_GRADIENT,
    tooltip: "Open and closed trades in this period.",
  },
  { id: "closed_trades", title: "Closed trades", backgroundGradient: COUNT_GRADIENT },
  {
    id: "open_trades",
    title: "Active trades",
    backgroundGradient: ACTIVE_TRADES_GRADIENT,
  },
  {
    id: "risk_reward_ratio",
    title: "Risk / reward",
    backgroundGradient: COUNT_GRADIENT,
    tooltip: "Average win ÷ average loss (absolute).",
  },
  { id: "average_win", title: "Average win" },
  {
    id: "average_loss",
    title: "Average loss",
    backgroundGradient: MINI_STAT_LOSS_GRADIENT,
  },
  { id: "largest_win", title: "Largest win" },
  {
    id: "largest_loss",
    title: "Largest loss",
    backgroundGradient: MINI_STAT_LOSS_GRADIENT,
  },
];

function buildEmptyMiniStats(): PerformanceSummaryMiniStat[] {
  return EMPTY_MINI_STAT_DEFS.map((def) => ({
    ...def,
    amount: "N/A",
  }));
}
